import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './CompanyAccount.css';
import lpApi from '../../helpers/lpApi';
import CompanyAuth from '../become-a-company/CompanyAuth';

// Company account section (web mirror of the mobile company profile): referral
// code (share/copy), manage services, change bank account (Stripe-hosted),
// privacy / legal / support, delete account. Auth via the shared CompanyAuth
// gate + id_token cookie, identical to /company/projects.

const Q_MY_COMPANY = `
  query {
    myCompanyProfile {
      status
      errorMessage
      companyProfile {
        id legalName kybStatus referralCode specialties
        stripeConnectAccountId stripeConnectTosAcceptedAt
        einLast4 websiteUrl yearFounded operatingStates
        headquartersAddressLine1 headquartersAddressLine2
        headquartersCity headquartersState headquartersZip
      }
    }
  }
`;

const Q_LICENSES = `
  query {
    myLicenses {
      status errorMessage
      licenses { id type number state classification issuingBody expirationDate verificationStatus }
    }
  }
`;

const Q_INSURANCES = `
  query {
    myInsurances {
      status errorMessage
      insurances { id type carrier policyNumber coverageAmount expirationDate verificationStatus }
    }
  }
`;

const M_UPDATE_SERVICES = `
  mutation ($specialties: [String]) {
    updateCompanyProfile(specialties: $specialties) {
      status
      errorMessage
      companyProfile { id specialties }
    }
  }
`;

const Q_PAYOUT_LINK = `
  query {
    companyPayoutUpdateLink {
      status
      errorMessage
      onboardingUrl
    }
  }
`;

const M_DELETE = `
  mutation {
    deleteUser {
      status
      errorMessage
    }
  }
`;

const SPECIALTIES = [
  ['KITCHEN', 'Kitchen'],
  ['BATHROOM', 'Bathroom'],
  ['BASEMENT', 'Basement'],
  ['EXTERIOR', 'Exterior'],
  ['ROOFING', 'Roofing'],
  ['INSTALLATION', 'Installation'],
  ['GENERAL', 'General Contracting'],
];

class CompanyAccount extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      authed: false,
      company: null,
      error: '',
      selected: [],
      savingServices: false,
      busy: false,
      licenses: [],
      insurances: [],
    };
    this.bootstrap = this.bootstrap.bind(this);
    this.onAuthenticated = this.onAuthenticated.bind(this);
  }

  componentDidMount() {
    this.bootstrap();
  }

  // eslint-disable-next-line class-methods-use-this
  firstError(res) {
    if (res && res.errors && res.errors.length) return res.errors[0].message;
    return 'Something went wrong.';
  }

  async bootstrap() {
    try {
      const res = await lpApi(Q_MY_COMPANY);
      if (res.errors) {
        const msg = this.firstError(res);
        const unauth = /401|unauth|log\s?in|token/i.test(msg);
        this.setState({ loading: false, authed: false, error: unauth ? '' : msg });
        return;
      }
      const cp = res.data && res.data.myCompanyProfile && res.data.myCompanyProfile.companyProfile;
      if (!cp) {
        // Logged in but no company yet -> point them to onboarding.
        this.setState({ loading: false, authed: true, company: null });
        return;
      }
      this.setState({
        loading: false,
        authed: true,
        company: cp,
        selected: (cp.specialties || []).filter(Boolean),
      });
      this.loadBusinessDocs();
    } catch (e) {
      this.setState({ loading: false, error: `Could not load: ${e.message}` });
    }
  }

  async loadBusinessDocs() {
    try {
      const [lRes, iRes] = await Promise.all([lpApi(Q_LICENSES), lpApi(Q_INSURANCES)]);
      const lic = lRes.data && lRes.data.myLicenses;
      const ins = iRes.data && iRes.data.myInsurances;
      this.setState({
        licenses: (lic && lic.status === 200 && lic.licenses) ? lic.licenses : [],
        insurances: (ins && ins.status === 200 && ins.insurances) ? ins.insurances : [],
      });
    } catch (e) {
      // non-fatal — Business section just shows empty states
    }
  }

  // eslint-disable-next-line class-methods-use-this
  formatAddress(cp) {
    if (!cp) return '';
    const cityState = [cp.headquartersCity, cp.headquartersState].filter(Boolean).join(', ');
    return [cp.headquartersAddressLine1, cp.headquartersAddressLine2, cityState, cp.headquartersZip]
      .map(x => (x || '').trim()).filter(Boolean).join(', ');
  }

  onAuthenticated() {
    this.setState({ loading: true, authed: true, error: '' }, () => this.bootstrap());
  }

  toggle(code) {
    const { selected } = this.state;
    if (selected.includes(code)) {
      this.setState({ selected: selected.filter(x => x !== code) });
    } else {
      this.setState({ selected: [...selected, code] });
    }
  }

  async saveServices() {
    const { selected } = this.state;
    if (!selected.length) {
      // eslint-disable-next-line no-alert
      alert('Select at least one service.');
      return;
    }
    this.setState({ savingServices: true });
    try {
      const res = await lpApi(M_UPDATE_SERVICES, { specialties: selected });
      const env = res.data && res.data.updateCompanyProfile;
      // eslint-disable-next-line no-alert
      if (env && env.status === 200) alert('Services updated');
      // eslint-disable-next-line no-alert
      else alert((env && env.errorMessage) || 'Could not update services.');
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Could not update services: ${e.message}`);
    } finally {
      this.setState({ savingServices: false });
    }
  }

  copyReferral() {
    const { company } = this.state;
    const code = (company && company.referralCode) || '';
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(code);
      // eslint-disable-next-line no-alert
      alert('Referral code copied');
    }
  }

  shareReferral() {
    const { company } = this.state;
    const code = (company && company.referralCode) || '';
    const msg = `Join BestHand for Pros! Use my referral code ${code} when your company signs up — we both earn rewards. https://besthandapp.com`;
    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator.share({ title: 'BestHand referral', text: msg }).catch(() => {});
    } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(msg);
      // eslint-disable-next-line no-alert
      alert('Invite message copied — paste it to share');
    }
  }

  async manageBank() {
    this.setState({ busy: true });
    try {
      const res = await lpApi(Q_PAYOUT_LINK);
      const env = res.data && res.data.companyPayoutUpdateLink;
      if (env && env.status === 200 && env.onboardingUrl) {
        window.location.href = env.onboardingUrl;
        return;
      }
      // eslint-disable-next-line no-alert
      alert((env && env.errorMessage) || 'Could not open the bank update page.');
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Could not open the bank update page: ${e.message}`);
    } finally {
      this.setState({ busy: false });
    }
  }

  async deleteAccount() {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Permanently delete your company account? This removes it from the marketplace and cannot be undone. You can\'t do this while a project is active.')) {
      return;
    }
    this.setState({ busy: true });
    try {
      const res = await lpApi(M_DELETE);
      const env = res.data && res.data.deleteUser;
      if (env && env.status === 200) {
        window.location.assign('/logout');
        return;
      }
      // eslint-disable-next-line no-alert
      alert((env && env.errorMessage) || 'Could not delete the account. Make sure no project is active.');
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Delete failed: ${e.message}`);
    } finally {
      this.setState({ busy: false });
    }
  }

  render() {
    const { loading, authed, company, selected, savingServices, busy } = this.state;

    if (loading) {
      return (
        <div className={s.page}>
          <div className={s.wrap}><div className={s.loading}>Loading your account…</div></div>
        </div>
      );
    }

    if (!authed) {
      return <CompanyAuth onAuthenticated={this.onAuthenticated} />;
    }

    if (!company) {
      return (
        <div className={s.page}>
          <div className={s.topbar}>
            <div className={s.wrap}>
              <div style={{ paddingTop: 22 }}>
                <span className={s.eyebrow}>BestHand Large Projects · for companies</span>
                <h1 className={s.title}>Company account</h1>
              </div>
            </div>
          </div>
          <div className={s.body}>
            <div className={s.wrap}>
              <div className={s.card}>
                <h2 className={s.cardTitle}>Finish setting up your company</h2>
                <p className={s.cardSub}>You're signed in, but you don't have a company profile yet.</p>
                <a className={s.btnPrimary} style={{ display: 'inline-block', textDecoration: 'none', textAlign: 'center' }} href="/become-a-company">Start onboarding</a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const code = company.referralCode || '';

    return (
      <div className={s.page}>
        <div className={s.topbar}>
          <div className={s.wrap}>
            <div style={{ paddingTop: 22 }}>
              <a href="/company/projects" className={s.eyebrow} style={{ textDecoration: 'none' }}>‹ Back to dashboard</a>
              <h1 className={s.title}>{company.legalName || 'Company account'}</h1>
            </div>
          </div>
        </div>

        <div className={s.body}>
          <div className={s.wrap}>
            {/* Referral */}
            {code ? (
              <div className={s.referralCard}>
                <div className={s.cardTitle}>Your referral code</div>
                <div className={s.referralCode}>{code}</div>
                <div className={s.cardSub}>Share it — earn cash in your payouts when companies you refer join and complete projects.</div>
                <div className={s.btnRow}>
                  <button type="button" className={s.btnOutline} onClick={() => this.copyReferral()}>Copy code</button>
                  <button type="button" className={s.btnPrimary} onClick={() => this.shareReferral()}>Share</button>
                </div>
              </div>
            ) : null}

            {/* Business: details, licenses, insurance, payouts */}
            <div className={s.card}>
              <h2 className={s.cardTitle}>Business</h2>
              <p className={s.cardSub}>Details, licenses, insurance &amp; payouts.</p>

              <div className={s.bizBlock}>
                <div className={s.bizHead}>Business details</div>
                <div className={s.bizRow}><span className={s.bizK}>EIN</span><span className={s.bizV}>{company.einLast4 ? `•••••${company.einLast4}` : '—'}</span></div>
                {this.formatAddress(company) ? (
                  <div className={s.bizRow}><span className={s.bizK}>Address</span><span className={s.bizV}>{this.formatAddress(company)}</span></div>
                ) : null}
                {company.websiteUrl ? (
                  <div className={s.bizRow}><span className={s.bizK}>Website</span><span className={s.bizV}>{company.websiteUrl}</span></div>
                ) : null}
                {company.yearFounded ? (
                  <div className={s.bizRow}><span className={s.bizK}>Founded</span><span className={s.bizV}>{company.yearFounded}</span></div>
                ) : null}
                {(company.operatingStates || []).filter(Boolean).length ? (
                  <div className={s.bizRow}><span className={s.bizK}>Operating states</span><span className={s.bizV}>{(company.operatingStates || []).filter(Boolean).join(', ')}</span></div>
                ) : null}
              </div>

              <div className={s.bizBlock}>
                <div className={s.bizHead}>Licenses</div>
                {licenses.length === 0 ? (
                  <div className={s.bizEmpty}>No licenses on file yet.</div>
                ) : licenses.map(l => (
                  <div key={l.id} className={s.docRow}>
                    <div className={s.docMain}>
                      <div className={s.docTitle}>{(l.type || 'License') + (l.classification ? ` · ${l.classification}` : '')}</div>
                      <div className={s.docSub}>{[l.number ? `#${l.number}` : null, l.state, l.expirationDate ? `exp ${String(l.expirationDate).slice(0, 10)}` : null].filter(Boolean).join(' · ')}</div>
                    </div>
                    {l.verificationStatus ? <span className={`${s.pill} ${l.verificationStatus === 'VERIFIED' ? s.pillOk : ''}`}>{l.verificationStatus}</span> : null}
                  </div>
                ))}
              </div>

              <div className={s.bizBlock}>
                <div className={s.bizHead}>Insurance</div>
                {insurances.length === 0 ? (
                  <div className={s.bizEmpty}>No insurance on file yet.</div>
                ) : insurances.map(i => (
                  <div key={i.id} className={s.docRow}>
                    <div className={s.docMain}>
                      <div className={s.docTitle}>{i.type || 'Policy'}</div>
                      <div className={s.docSub}>{[i.carrier, i.coverageAmount ? `$${i.coverageAmount}` : null, i.expirationDate ? `exp ${String(i.expirationDate).slice(0, 10)}` : null].filter(Boolean).join(' · ')}</div>
                    </div>
                    {i.verificationStatus ? <span className={`${s.pill} ${i.verificationStatus === 'VERIFIED' ? s.pillOk : ''}`}>{i.verificationStatus}</span> : null}
                  </div>
                ))}
              </div>

              <div className={s.bizBlock}>
                <div className={s.bizHead}>Payouts</div>
                <div className={s.docRow}>
                  <div className={s.docMain}>
                    <div className={s.docSub}>Milestone payouts via Stripe</div>
                  </div>
                  <span className={`${s.pill} ${company.stripeConnectAccountId && company.stripeConnectTosAcceptedAt ? s.pillOk : ''}`}>
                    {company.stripeConnectAccountId ? (company.stripeConnectTosAcceptedAt ? 'ACTIVE' : 'IN_PROGRESS') : 'PENDING'}
                  </span>
                </div>
              </div>
            </div>

            {/* Manage services */}
            <div className={s.card}>
              <h2 className={s.cardTitle}>Manage services</h2>
              <p className={s.cardSub}>The services your company offers.</p>
              <div className={s.chips}>
                {SPECIALTIES.map(([cde, label]) => {
                  const on = selected.includes(cde);
                  return (
                    <span
                      key={cde}
                      role="button"
                      tabIndex={0}
                      className={`${s.chip} ${on ? s.chipOn : ''}`}
                      onClick={() => this.toggle(cde)}
                      onKeyPress={() => this.toggle(cde)}
                    >
                      {on ? '✓ ' : '+ '}{label}
                    </span>
                  );
                })}
              </div>
              <div className={s.saveRow}>
                <button type="button" className={s.btnPrimary} style={{ maxWidth: 160 }} disabled={savingServices} onClick={() => this.saveServices()}>
                  {savingServices ? 'Saving…' : 'Save services'}
                </button>
              </div>
            </div>

            {/* Bank account */}
            <div className={s.card}>
              <h2 className={s.cardTitle}>Bank account</h2>
              <p className={s.cardSub}>Change the bank account your milestone payouts go to. You'll be taken to a secure Stripe page.</p>
              {company.stripeConnectAccountId ? (
                <button type="button" className={s.btnPrimary} style={{ maxWidth: 220 }} disabled={busy} onClick={() => this.manageBank()}>
                  {busy ? 'Opening…' : 'Change bank account'}
                </button>
              ) : (
                <p className={s.kv}>Complete onboarding to set up payouts first.</p>
              )}
            </div>

            {/* Legal / support */}
            <div className={s.card}>
              <a className={s.row} href="/large-projects-terms">
                <span className={s.rowMain}>
                  <span className={s.rowTitle}>Large Projects Terms</span>
                  <span className={s.rowSub}>Escrow, milestones, lien waivers</span>
                </span>
                <span className={s.chevron}>›</span>
              </a>
              <a className={s.row} href="/company-terms">
                <span className={s.rowMain}>
                  <span className={s.rowTitle}>Company &amp; Contractor Agreement</span>
                  <span className={s.rowSub}>Verification, licensing, payouts</span>
                </span>
                <span className={s.chevron}>›</span>
              </a>
              <a className={s.row} href="/privacy-policy">
                <span className={s.rowMain}><span className={s.rowTitle}>Privacy policy</span></span>
                <span className={s.chevron}>›</span>
              </a>
              <a className={s.row} href="/legal">
                <span className={s.rowMain}><span className={s.rowTitle}>Legal</span></span>
                <span className={s.chevron}>›</span>
              </a>
              <a className={s.row} href="/support">
                <span className={s.rowMain}>
                  <span className={s.rowTitle}>Support</span>
                  <span className={s.rowSub}>Get help & contact us</span>
                </span>
                <span className={s.chevron}>›</span>
              </a>
            </div>

            {/* Delete */}
            <div className={s.card}>
              <h2 className={s.cardTitle}>Delete account</h2>
              <p className={s.cardSub}>Permanently delete your company account and remove it from the marketplace. Not available while a project is active.</p>
              <button type="button" className={s.btnOutline} style={{ maxWidth: 200 }} disabled={busy} onClick={() => this.deleteAccount()}>
                Delete account
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withStyles(s)(CompanyAccount);
